<template>
  <div>
    <PageHeader
      title="Problems"
      subtitle="Beta — practice constraint nodes"
      :breadcrumbs="[{ label: 'Beta' }]"
    />

    <div class="p-6 space-y-4">
      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-4">
          <div class="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button
              v-for="dim in dimensionTabs"
              :key="dim.key"
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="dimension === dim.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              @click="dimension = dim.key"
            >
              {{ dim.label }}
            </button>
          </div>

          <div v-if="dimension === 'category'" class="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="!showSubcategory ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              @click="showSubcategory = false"
            >
              Category
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="showSubcategory ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              @click="showSubcategory = true"
            >
              Subcategory
            </button>
          </div>

          <div v-if="!subcategoryActive" class="h-5 w-px bg-gray-200"></div>

          <button
            v-if="!subcategoryActive"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
            :class="splitByPace ? 'border-accent bg-accent-50 text-accent' : 'border-gray-200 text-gray-500 hover:text-gray-800'"
            @click="splitByPace = !splitByPace"
          >
            <span aria-hidden="true">↗</span>
            Split by initiative pace
          </button>

          <div v-if="splitByPace && !subcategoryActive" class="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="paceMode === 'count' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              @click="paceMode = 'count'"
            >
              Count
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="paceMode === 'rate' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              @click="paceMode = 'rate'"
            >
              Rate
            </button>
          </div>
        </div>

        <div
          v-if="splitByPace && initiativeOptions.length"
          class="px-4 py-2.5 border-b border-gray-200 flex flex-wrap items-center gap-2"
        >
          <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-1">Initiatives</span>
          <button
            v-for="opt in initiativeOptions"
            :key="opt.value"
            type="button"
            class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border transition-colors"
            :class="selected.initiative.includes(opt.value)
              ? 'border-accent bg-accent-50 text-accent'
              : 'border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300'"
            @click="toggleInitiative(opt.value)"
          >
            {{ opt.label }}
          </button>
          <button
            v-if="selected.initiative.length"
            type="button"
            class="text-xs text-gray-400 hover:text-gray-700 ml-1"
            @click="selected.initiative = []"
          >
            Clear
          </button>
        </div>

        <div class="p-4">
          <LoadingIndicator v-if="loading" message="Loading problems…" />
          <div
            v-else-if="error"
            class="min-h-[200px] rounded-lg bg-red-50 p-4 text-sm text-red-800"
          >
            {{ error }}
          </div>
          <div
            v-else-if="!chartHasData"
            class="min-h-[160px] flex items-center justify-center text-sm text-gray-400"
          >
            No problems match the current filters.
          </div>
          <div v-else>
            <p v-if="chartCaption" class="text-[11px] text-gray-400 mb-3">
              {{ chartCaption }}
            </p>
            <div :class="chartSpec.mode === 'grouped' ? 'lg:columns-2 lg:gap-x-10' : ''">
              <div
                v-for="g in chartSpec.groups"
                :key="g.key"
                :class="chartSpec.mode === 'grouped' ? 'break-inside-avoid mb-5' : ''"
              >
                <div v-if="g.label" class="flex items-baseline gap-3 mb-1 pb-1 border-b border-gray-100">
                  <div class="flex items-center gap-1.5 text-xs font-semibold text-gray-700 min-w-0" :title="g.label">
                    <span v-if="g.headerColor" class="inline-block w-2 h-2 rounded-sm shrink-0" :style="{ background: g.headerColor }"></span>
                    <span class="truncate">{{ g.label }}</span>
                  </div>
                  <div class="flex-1"></div>
                  <div v-if="g.total != null" class="shrink-0 text-[11px] tabular-nums text-gray-400 text-right">{{ g.total }}</div>
                </div>
                <div
                  v-for="row in g.rows"
                  :key="row.key"
                  class="group flex items-center gap-3 py-0.5"
                >
                  <div
                    class="shrink-0 text-xs text-gray-600 truncate"
                    :class="[chartSpec.labelClass, chartSpec.mode === 'grouped' ? 'pl-3' : '']"
                    :title="row.label"
                  >
                    <span v-if="row.icon" class="mr-1 font-semibold" :style="{ color: row.iconColor }" aria-hidden="true">{{ row.icon }}</span>{{ row.label }}
                  </div>
                  <div class="flex-1 min-w-0 bg-gray-50 rounded">
                    <div
                      class="h-5 rounded transition-[width] duration-300 group-hover:brightness-95"
                      :style="{ width: Math.max(1.5, (row.value / chartSpec.max) * 100) + '%', background: row.color }"
                      :title="row.tip || (row.label + ': ' + row.rawCount)"
                    ></div>
                  </div>
                  <div class="w-16 shrink-0 text-xs tabular-nums font-medium text-gray-700 text-right">
                    {{ fmtVal(row.value) }}<span v-if="chartSpec.valueFmt === 'rate'" class="text-gray-400 text-[10px] font-normal">/100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-end gap-4">
          <div class="w-56">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Practice</label>
            <MultiSelectDropdown
              v-model="selected.practice"
              :options="practiceOptions"
              searchable
              placeholder="All practices"
            />
          </div>
          <div class="w-56">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Category</label>
            <MultiSelectDropdown
              v-model="selected.category"
              :options="categoryOptions"
              placeholder="All categories"
            />
          </div>
          <div class="w-56">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Subcategory</label>
            <MultiSelectDropdown
              v-model="selected.subcategory"
              :options="subcategoryOptions"
              searchable
              placeholder="All subcategories"
            />
          </div>
          <div class="w-52">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Gap type</label>
            <MultiSelectDropdown
              v-model="selected.gap_type"
              :options="gapTypeOptions"
              placeholder="All gap types"
            />
          </div>
          <div class="w-52">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Disposition</label>
            <MultiSelectDropdown
              v-model="selected.disposition"
              :options="dispositionOptions"
              placeholder="All dispositions"
            />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Scope</label>
            <MultiSelectDropdown
              v-model="selected.scope"
              :options="scopeOptions"
              placeholder="All scopes"
            />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Status</label>
            <MultiSelectDropdown
              v-model="selected.status"
              :options="statusOptions"
              placeholder="All statuses"
            />
          </div>
          <div class="w-44">
            <label class="block text-xs font-semibold text-gray-600 mb-1">Group by</label>
            <FilterDropdown v-model="groupBy" :options="groupByOptions" />
          </div>
          <button
            v-if="hasActiveFilters"
            class="text-sm text-gray-500 hover:text-gray-800 pb-2"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>

        <div class="p-4">
          <LoadingIndicator v-if="loading" message="Loading problems…" />
          <div
            v-else-if="error"
            class="min-h-[200px] rounded-lg bg-red-50 p-4 text-sm text-red-800"
          >
            {{ error }}
          </div>
          <div v-else class="overflow-auto max-h-[640px] border border-gray-200 rounded-lg">
            <table class="w-full text-xs">
              <thead class="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                <tr>
                  <th class="px-3 py-2 text-left">Practice</th>
                  <th class="px-3 py-2 text-left">Problem</th>
                  <th class="px-3 py-2 text-left">Category</th>
                  <th class="px-3 py-2 text-left">Subcategory</th>
                  <th class="px-3 py-2 text-left">Gap type</th>
                  <th class="px-3 py-2 text-left">Scope</th>
                  <th class="px-3 py-2 text-left">Disposition</th>
                  <th class="px-3 py-2 text-left">Status</th>
                  <th class="px-3 py-2 text-left">Initiative</th>
                  <th class="px-3 py-2 text-left">Severity</th>
                  <th class="px-3 py-2 text-left">Owner</th>
                  <th class="px-3 py-2 text-left">Raised</th>
                  <th class="px-3 py-2 text-left">Last seen</th>
                  <th class="px-3 py-2 text-left">Closed</th>
                  <th class="px-3 py-2 text-right">Mentions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <template v-for="group in groupedRows" :key="group.key">
                  <tr v-if="group.label !== null" class="bg-gray-100">
                    <td colspan="15" class="px-3 py-1.5 text-xs font-semibold text-gray-700">
                      {{ group.label }}
                      <span class="text-gray-400 font-normal">· {{ group.rows.length }}</span>
                    </td>
                  </tr>
                  <tr
                    v-for="problem in group.rows"
                    :key="problem.id"
                    class="hover:bg-gray-50"
                  >
                    <td class="px-3 py-2">{{ problem.practice_name }}</td>
                    <td class="px-3 py-2 font-medium" :class="isDimmed(problem) ? 'text-gray-400' : 'text-gray-900'">{{ problem.title }}</td>
                    <td class="px-3 py-2">
                      <span v-if="problem.category" :class="badgeBase" :style="categoryStyle(problem.category)">
                        {{ categoryLabel(problem.category) }}
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-3 py-2 text-gray-600">{{ subcategoryLabel(problem.subcategory) }}</td>
                    <td class="px-3 py-2">
                      <span v-if="problem.gap_type" :class="badgeBase" :style="gapTypeStyle(problem.gap_type)">
                        {{ gapTypeLabel(problem.gap_type) }}
                      </span>
                      <span
                        v-else
                        :class="badgeBase"
                        :style="{ background: '#f8fafc', color: '#94a3b8' }"
                        title="No practice-actionable gap — external, structural, or patient-driven"
                      >not actionable</span>
                    </td>
                    <td class="px-3 py-2">
                      <span v-if="problem.scope === 'provider'" class="text-gray-700">{{ problem.subject_provider_name || 'Provider' }}</span>
                      <span v-else class="text-gray-400">Practice</span>
                    </td>
                    <td class="px-3 py-2">
                      <span v-if="problem.disposition" :class="badgeBase" :style="dispositionStyle(problem.disposition)">
                        {{ dispositionLabel(problem.disposition) }}
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-3 py-2">
                      <span :class="badgeBase" :style="statusStyleFor(problem.status)">
                        <span aria-hidden="true" class="leading-none">{{ statusGlyph(problem.status) }}</span>
                        {{ statusLabel(problem.status) }}
                      </span>
                    </td>
                    <td class="px-3 py-2 text-gray-600">{{ initiativeLabel(problem.initiative_area) }}</td>
                    <td class="px-3 py-2">
                      <span v-if="problem.severity" :class="badgeBase" :style="severityStyle(problem.severity)">
                        {{ problem.severity }}
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-3 py-2 text-gray-600">{{ problem.owner_name || '—' }}</td>
                    <td class="px-3 py-2 text-gray-500 tabular-nums">{{ formatDate(problem.raised_at) }}</td>
                    <td class="px-3 py-2 text-gray-500 tabular-nums">{{ formatDate(problem.last_seen_at) }}</td>
                    <td class="px-3 py-2 text-gray-500 tabular-nums">{{ formatDate(problem.closed_at) }}</td>
                    <td class="px-3 py-2 text-right tabular-nums">{{ problem.mention_count }}</td>
                  </tr>
                </template>
                <tr v-if="!filteredProblems.length">
                  <td colspan="15" class="px-3 py-8 text-center text-gray-400">
                    No problems match the current filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import {
  fmt, HEALTH, NEUTRAL, severityStyle, STATUS_OPTIONS, SEVERITY_OPTIONS, INITIATIVE_ORDER,
} from '@/components/EngagementTracker/engagementTrackerHelpers.js';

// The lifecycle axis (renamed from "category"). Order + colors as before.
const DISPOSITION_ORDER = ['unaddressed', 'being_worked', 'worked_around', 'fixed', 'unable', 'abandoned'];

const NEUTRAL_STYLE = { background: NEUTRAL.tint, color: NEUTRAL.ink };

// Softer bar fills that read like the table's tag pills, not saturated blocks: mix the tag's ink
// color toward white so it stays the same hue family but calmer.
function soften(hex, mix = 0.5) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const to = v => Math.round(v + (255 - v) * mix).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

const DISPOSITION_STYLE = {
  unaddressed: { background: NEUTRAL.tint, color: NEUTRAL.ink },
  being_worked: { background: HEALTH.on_track.tint, color: HEALTH.on_track.hex },
  worked_around: { background: '#FEF3C7', color: '#B45309' },
  fixed: { background: HEALTH.ahead.tint, color: HEALTH.ahead.hex },
  unable: { background: HEALTH.behind.tint, color: HEALTH.behind.hex },
  abandoned: { background: NEUTRAL.tint, color: '#9ca3af' },
};

// The subject axis (the 9 controlled categories). One calm color each.
const CATEGORY_STYLE = {
  data_integrity: { background: '#EFF6FF', color: '#1D4ED8' },
  coding: { background: '#F5F3FF', color: '#6D28D9' },
  tooling_access: { background: '#ECFEFF', color: '#0E7490' },
  transitions_of_care: { background: '#FEF2F2', color: '#B91C1C' },
  scheduling: { background: '#FFF7ED', color: '#C2410C' },
  capacity: { background: '#FEFCE8', color: '#A16207' },
  patient: { background: '#F0FDF4', color: '#15803D' },
  utilization_referrals: { background: '#FDF4FF', color: '#A21CAF' },
  payment_contract: { background: '#F1F5F9', color: '#334155' },
};

// The mechanism axis (why the work isn't happening).
const GAP_TYPE_STYLE = {
  knowledge: { background: '#EFF6FF', color: '#1D4ED8' },
  capacity: { background: '#FFF7ED', color: '#C2410C' },
  ownership: { background: '#F5F3FF', color: '#6D28D9' },
  motivation: { background: '#FDF2F8', color: '#BE185D' },
  other: { background: '#F1F5F9', color: '#475569' },
};

const STATUS_STYLE = {
  open: { background: '#f3f4f6', color: '#4b5563' },
  in_progress: { background: '#e5e7eb', color: '#374151' },
  resolved: { background: '#f3f4f6', color: '#9ca3af' },
  dropped: { background: '#f3f4f6', color: '#9ca3af' },
};

// Scope — whether a problem is provider-specific or shared across the practice.
const SCOPE_STYLE = {
  provider: { background: '#ECFEFF', color: '#0E7490' },
  practice: { background: '#F1F5F9', color: '#475569' },
};

// initiative_area is stored as a raw key; humanize to match the Engagement Tracker's spine labels.
const INITIATIVE_LABELS = {
  awv_completion: 'AWV Completion',
  visit_coverage: 'Visit Coverage',
  risk_documentation: 'Risk Documentation',
  transitions_of_care: 'Transitions of Care',
  contract_payments: 'Contract & Payments',
};

const STATUS_RANK = STATUS_OPTIONS.reduce((map, status, i) => {
  map[status] = i;
  return map;
}, {});

// The dimensions the top bar graph can pivot across (single-select).
const DIMENSION_TABS = [
  { key: 'category', label: 'Category' },
  { key: 'gap_type', label: 'Gap type' },
  { key: 'disposition', label: 'Disposition' },
  { key: 'status', label: 'Status' },
  { key: 'severity', label: 'Severity' },
  { key: 'scope', label: 'Scope' },
];

// Table "Group by" options (single-select — rendered with the shared FilterDropdown so it matches
// the filter dropdowns instead of a native OS <select>).
const GROUP_BY_OPTIONS = [
  { value: 'practice', label: 'Practice' },
  { value: 'category', label: 'Category' },
  { value: 'subcategory', label: 'Subcategory' },
  { value: 'gap_type', label: 'Gap type' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'scope', label: 'Scope' },
  { value: 'status', label: 'Status' },
  { value: 'none', label: 'None' },
];

// Initiative-pace segments (the ET health language), in fixed stack/legend order. '__none__' = the
// problem has no linked initiative (or the initiative has no trajectory yet).
const PACE_ORDER = ['ahead', 'on_track', 'mixed', 'behind', '__none__'];
const PACE_META = {
  ahead: { label: 'Ahead', color: HEALTH.ahead.hex, icon: '↗' },
  on_track: { label: 'On track', color: HEALTH.on_track.hex, icon: '→' },
  mixed: { label: 'Mixed', color: '#9CA3AF', icon: '↔' },
  behind: { label: 'Behind', color: HEALTH.behind.hex, icon: '↘' },
  __none__: { label: 'No initiative', color: '#D1D5DB', icon: '·' },
};

// Field on each problem row backing each filterable dimension.
const DIMENSIONS = [
  { key: 'practice', field: 'practice_entity_id' },
  { key: 'category', field: 'category' },
  { key: 'subcategory', field: 'subcategory' },
  { key: 'gap_type', field: 'gap_type' },
  { key: 'disposition', field: 'disposition' },
  { key: 'scope', field: 'scope' },
  { key: 'status', field: 'status' },
  { key: 'initiative', field: 'initiative_area' },
];

export default {
  name: 'ProblemsBeta',
  components: { PageHeader, LoadingIndicator, MultiSelectDropdown, FilterDropdown },
  data() {
    return {
      loading: false,
      error: null,
      practices: [],
      problems: [],
      taxonomy: {
        categories: [],
        other: { key: 'other', label: 'Other' },
        dispositions: [],
        gap_types: [],
        scopes: [],
      },
      selected: {
        practice: [],
        category: [],
        subcategory: [],
        gap_type: [],
        disposition: [],
        scope: [],
        status: [],
        initiative: [],
      },
      groupBy: 'practice',
      groupByOptions: GROUP_BY_OPTIONS,
      dimension: 'category',
      showSubcategory: false,
      splitByPace: true,
      paceMode: 'rate',
      initiatives: [],
      dimensionTabs: DIMENSION_TABS,
      badgeBase: 'inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 whitespace-nowrap',
    };
  },
  computed: {
    toast() {
      return useToast();
    },
    categoryOrder() {
      return this.taxonomy.categories.map(c => c.key);
    },
    categoryLabelMap() {
      const map = {};
      for (const c of this.taxonomy.categories) map[c.key] = c.label;
      return map;
    },
    subcategoryLabelMap() {
      const map = { [this.taxonomy.other.key]: this.taxonomy.other.label };
      for (const c of this.taxonomy.categories) {
        for (const s of c.subcategories) map[s.key] = s.label;
      }
      return map;
    },
    subcategoryOrder() {
      const order = [];
      for (const c of this.taxonomy.categories) {
        for (const s of c.subcategories) order.push(s.key);
      }
      order.push(this.taxonomy.other.key);
      return order;
    },
    practiceOptions() {
      return this.practices.map(practice => ({
        value: practice.entity_id,
        label: practice.practice_name,
      }));
    },
    categoryOptions() {
      return this.presentValues('category', this.categoryOrder).map(value => ({
        value,
        label: this.categoryLabel(value),
      }));
    },
    subcategoryOptions() {
      return this.presentValues('subcategory', this.subcategoryOrder).map(value => ({
        value,
        label: this.subcategoryLabel(value),
      }));
    },
    gapTypeOptions() {
      return this.presentValues('gap_type', this.taxonomy.gap_types).map(value => ({
        value,
        label: this.gapTypeLabel(value),
      }));
    },
    dispositionOptions() {
      return this.presentValues('disposition', DISPOSITION_ORDER).map(value => ({
        value,
        label: this.dispositionLabel(value),
      }));
    },
    scopeOptions() {
      return this.presentValues('scope', this.taxonomy.scopes).map(value => ({
        value,
        label: this.scopeLabel(value),
      }));
    },
    statusOptions() {
      return this.presentValues('status', STATUS_OPTIONS).map(value => ({
        value,
        label: this.statusLabel(value),
      }));
    },
    initiativeOptions() {
      return this.presentValues('initiative_area', INITIATIVE_ORDER).map(value => ({
        value,
        label: this.initiativeLabel(value),
      }));
    },
    // Per-pace initiative counts backing the Rate bars, scoped to the active initiative-side filters
    // (Initiative + Practice) so a bar = problems per 100 initiatives OF THE SELECTED initiative/
    // practice at that pace — numerator and denominator drawn from the same population. Problem-side
    // filters (category, gap type, status, …) narrow only the numerator; they aren't initiative facts.
    paceDenominators() {
      const initSel = this.selected.initiative;
      const practiceSel = this.selected.practice;
      const counts = {};
      for (const init of this.initiatives) {
        if (!init.pace) continue;
        if (initSel.length && !initSel.includes(init.initiative_key)) continue;
        if (practiceSel.length && !practiceSel.includes(init.entity_id)) continue;
        counts[init.pace] = (counts[init.pace] || 0) + 1;
      }
      return counts;
    },
    hasActiveFilters() {
      return DIMENSIONS.some(d => this.selected[d.key].length > 0);
    },
    filteredProblems() {
      return this.problems.filter(problem => this.matches(problem, null));
    },
    sortedProblems() {
      return [...this.filteredProblems].sort((a, b) => {
        const byPractice = (a.practice_name || '').localeCompare(b.practice_name || '');
        if (byPractice !== 0) return byPractice;
        return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
      });
    },
    groupedRows() {
      if (this.groupBy === 'none') {
        return [{ key: 'all', label: null, rows: this.sortedProblems }];
      }
      const groups = [];
      const byKey = {};
      for (const problem of this.sortedProblems) {
        const { key, label } = this.groupKeyFor(problem);
        if (!byKey[key]) {
          byKey[key] = { key, label, rows: [] };
          groups.push(byKey[key]);
        }
        byKey[key].rows.push(problem);
      }
      return groups;
    },
    // True when the subcategory drill-in is the active view (category dimension only). Pace-split is
    // hidden then, so the top chart never has to render a category×pace grid.
    subcategoryActive() {
      return this.dimension === 'category' && this.showSubcategory;
    },
    // The x-axis config (which problem field, its canonical order, and label fn) for the current view.
    xConfig() {
      if (this.subcategoryActive) {
        return { field: 'subcategory', order: this.subcategoryOrder, labelFn: this.subcategoryLabel, nullLabel: '—' };
      }
      switch (this.dimension) {
        case 'gap_type':
          return { field: 'gap_type', order: this.taxonomy.gap_types, labelFn: this.gapTypeLabel, nullLabel: 'Not actionable' };
        case 'disposition':
          return { field: 'disposition', order: DISPOSITION_ORDER, labelFn: this.dispositionLabel, nullLabel: '—' };
        case 'status':
          return { field: 'status', order: STATUS_OPTIONS, labelFn: this.statusLabel, nullLabel: '—' };
        case 'severity':
          return { field: 'severity', order: SEVERITY_OPTIONS, labelFn: this.labelize, nullLabel: '—' };
        case 'scope':
          return { field: 'scope', order: this.taxonomy.scopes, labelFn: this.scopeLabel, nullLabel: '—' };
        default:
          return { field: 'category', order: this.categoryOrder, labelFn: this.categoryLabel, nullLabel: '—' };
      }
    },
    subcatParent() {
      const map = {};
      for (const c of this.taxonomy.categories) {
        for (const s of c.subcategories) map[s.key] = c.key;
      }
      return map;
    },
    // One horizontal bar chart on a single shared axis: { mode:'flat'|'grouped', groups:[{key,label,
    // headerColor,total,rows:[{key,label,value,rawCount,color}]}], max, valueFmt }. flat = a plain
    // dimension ranking; grouped = section header + indented sub-bars (pace-split, or subcategory).
    chartSpec() {
      const probs = this.filteredProblems;
      if (this.splitByPace) return this.paceSpec(probs, this.xConfig);
      if (this.subcategoryActive) return this.subcategorySpec(probs);
      return this.flatSpec(probs, this.xConfig);
    },
    chartHasData() {
      return this.chartSpec.groups.some(g => g.rows.length);
    },
    chartCaption() {
      if (!this.splitByPace) return '';
      return this.paceMode === 'rate'
        ? 'Each bar = problems per 100 initiatives at that pace, so paces compare fairly regardless of how common each is (hover a bar for the math). Bars use different denominators, so they are not parts of a category total.'
        : "Each category's problems split by how their initiative is tracking vs its target.";
    },
  },
  watch: {
    // Keep the two second-level groupings mutually exclusive, and drop the subcategory drill-in when
    // it no longer applies to the chosen dimension.
    splitByPace(on) {
      if (on) this.showSubcategory = false;
      // The initiative chips only live in the pace-split view; drop the filter when leaving it so
      // it can't linger as a hidden constraint on the table.
      else this.selected.initiative = [];
    },
    showSubcategory(on) {
      if (on) this.splitByPace = false;
    },
    dimension(value) {
      if (value !== 'category') this.showSubcategory = false;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    severityStyle,
    statusLabel: fmt.statusLabel,
    statusGlyph: fmt.statusGlyph,
    formatDate: fmt.formatDate,
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/problems/');
        this.practices = data.practices || [];
        this.problems = data.problems || [];
        this.initiatives = data.initiatives || [];
        if (data.taxonomy) this.taxonomy = { ...this.taxonomy, ...data.taxonomy };
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        this.toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    matches(problem, skipKey) {
      for (const d of DIMENSIONS) {
        if (d.key === skipKey) continue;
        const sel = this.selected[d.key];
        if (sel.length && !sel.includes(problem[d.field])) return false;
      }
      return true;
    },
    presentValues(field, canonical) {
      const order = canonical || [];
      const present = new Set(this.problems.map(problem => problem[field]).filter(Boolean));
      const known = order.filter(value => present.has(value));
      const extra = [...present].filter(value => !order.includes(value)).sort((a, b) => a.localeCompare(b));
      return [...known, ...extra];
    },
    // Ordered [{ key, label, count }] for a set of problems along one dimension. Canonical order first,
    // then any extras; zero counts dropped; nulls collapse into a single trailing nullLabel entry.
    entriesFor(problems, cfg) {
      const counts = new Map();
      let nullCount = 0;
      for (const p of problems) {
        const v = p[cfg.field];
        if (v == null || v === '') { nullCount += 1; continue; }
        counts.set(v, (counts.get(v) || 0) + 1);
      }
      const order = cfg.order || [];
      const known = order.filter(value => counts.has(value));
      const extra = [...counts.keys()]
        .filter(value => !order.includes(value))
        .sort((a, b) => String(a).localeCompare(String(b)));
      const entries = [...known, ...extra].map(value => ({
        key: value, label: cfg.labelFn(value), count: counts.get(value),
      }));
      if (nullCount > 0) entries.push({ key: null, label: cfg.nullLabel, count: nullCount });
      return entries;
    },
    // Plain ranking: one bar per dimension value, colored (softly) by that value's own badge color.
    flatSpec(problems, cfg) {
      const entries = this.entriesFor(problems, cfg).sort((a, b) => b.count - a.count);
      const rows = entries.map(e => ({
        key: e.key == null ? '__null__' : e.key,
        label: e.label,
        value: e.count,
        rawCount: e.count,
        color: soften(this.dimColor(cfg.field, e.key)),
      }));
      return {
        mode: 'flat',
        labelClass: 'w-56',
        groups: [{ key: '__all__', label: null, headerColor: null, total: 0, rows }],
        max: Math.max(1, ...rows.map(r => r.value)),
        valueFmt: 'count',
      };
    },
    // Grouped: one section per dimension value, its rows the pace sub-bars (side by side to compare
    // length). In Rate mode each bar = problems per 100 initiatives at that pace ('No initiative'
    // dropped — no denominator); in Count mode it's the raw count.
    paceSpec(problems, cfg) {
      const rate = this.paceMode === 'rate';
      const denom = this.paceDenominators || {};
      const presentPaces = PACE_ORDER.filter(pk =>
        (!rate || pk !== '__none__') && problems.some(p => (p.initiative_pace || '__none__') === pk));
      const byVal = new Map();
      for (const p of problems) {
        let v = p[cfg.field];
        if (v == null || v === '') v = '__null__';
        const pace = p.initiative_pace || '__none__';
        if (!byVal.has(v)) byVal.set(v, {});
        const m = byVal.get(v);
        m[pace] = (m[pace] || 0) + 1;
      }
      let max = 0;
      const groups = this.entriesFor(problems, cfg)
        .map(e => {
          const vk = e.key == null ? '__null__' : e.key;
          const m = byVal.get(vk) || {};
          const rows = presentPaces
            .filter(pk => m[pk])
            .map(pk => {
              const raw = m[pk];
              const d = denom[pk] || 0;
              const value = rate ? (d ? (raw / d) * 100 : 0) : raw;
              max = Math.max(max, value);
              return {
                key: pk,
                label: PACE_META[pk].label,
                icon: PACE_META[pk].icon,
                iconColor: PACE_META[pk].color,
                value,
                rawCount: raw,
                color: soften(PACE_META[pk].color),
                // Rate bars can't share a denominator, so no meaningful category total — spell the
                // math out per bar on hover instead.
                tip: rate
                  ? `${raw} problems ÷ ${d} ${PACE_META[pk].label.toLowerCase()}-pace initiatives = ${Math.round(value)} per 100`
                  : null,
              };
            });
          // In Rate mode the bars use different (per-pace) denominators, so a category total would be
          // apples-to-oranges — omit it. In Count mode the total is the sum of the bars.
          const total = rate ? null : e.count;
          return { key: vk, label: e.label, headerColor: null, total, rawTotal: e.count, rows };
        })
        .filter(g => g.rows.length)
        .sort((a, b) => b.rawTotal - a.rawTotal);
      return { mode: 'grouped', labelClass: 'w-32', groups, max: Math.max(rate ? 0.001 : 1, max), valueFmt: rate ? 'rate' : 'count' };
    },
    // Grouped: one section per parent category (header + total), its rows the subcategories (indented),
    // all bars in the parent's color. No legend — the header names the group.
    subcategorySpec(problems) {
      const byCat = new Map();
      const catTotals = new Map();
      for (const p of problems) {
        const cat = p.category || '__uncat__';
        const sub = p.subcategory || '__null__';
        if (!byCat.has(cat)) byCat.set(cat, new Map());
        const m = byCat.get(cat);
        m.set(sub, (m.get(sub) || 0) + 1);
        catTotals.set(cat, (catTotals.get(cat) || 0) + 1);
      }
      const orderedCats = this.taxonomy.categories.map(c => c.key).filter(k => byCat.has(k));
      if (byCat.has('__uncat__')) orderedCats.push('__uncat__');
      let max = 1;
      const groups = orderedCats.map(catKey => {
        const isUncat = catKey === '__uncat__';
        const color = soften(isUncat ? NEUTRAL.hex : (CATEGORY_STYLE[catKey] || NEUTRAL_STYLE).color);
        const rows = [...byCat.get(catKey).entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([sub, count]) => {
            max = Math.max(max, count);
            return {
              key: `${catKey}|${sub}`,
              label: sub === '__null__' ? '—' : this.subcategoryLabel(sub),
              value: count,
              rawCount: count,
              color,
            };
          });
        return {
          key: catKey,
          label: isUncat ? 'Uncategorized' : this.categoryLabel(catKey),
          headerColor: color,
          total: catTotals.get(catKey),
          rows,
        };
      });
      return { mode: 'grouped', labelClass: 'w-52', groups, max, valueFmt: 'count' };
    },
    fmtVal(value) {
      return this.chartSpec.valueFmt === 'rate' ? Math.round(value) : value;
    },
    // Fill color for a single-dimension bar — the value's own badge color (matches the table below).
    dimColor(field, key) {
      if (key == null) return '#cbd5e1';
      if (field === 'category') return (CATEGORY_STYLE[key] || NEUTRAL_STYLE).color;
      if (field === 'subcategory') return (CATEGORY_STYLE[this.subcatParent[key]] || NEUTRAL_STYLE).color;
      if (field === 'gap_type') return (GAP_TYPE_STYLE[key] || NEUTRAL_STYLE).color;
      if (field === 'disposition') return (DISPOSITION_STYLE[key] || NEUTRAL_STYLE).color;
      if (field === 'status') return (STATUS_STYLE[key] || NEUTRAL_STYLE).color;
      if (field === 'severity') return severityStyle(key).color;
      if (field === 'scope') return (SCOPE_STYLE[key] || NEUTRAL_STYLE).color;
      return HEALTH.on_track.hex;
    },
    groupKeyFor(problem) {
      if (this.groupBy === 'practice') {
        return { key: problem.practice_entity_id, label: problem.practice_name };
      }
      if (this.groupBy === 'category') {
        return { key: problem.category || '—', label: this.categoryLabel(problem.category) };
      }
      if (this.groupBy === 'subcategory') {
        return { key: problem.subcategory || '—', label: this.subcategoryLabel(problem.subcategory) };
      }
      if (this.groupBy === 'gap_type') {
        return {
          key: problem.gap_type || '__na__',
          label: problem.gap_type ? this.gapTypeLabel(problem.gap_type) : 'Not actionable',
        };
      }
      if (this.groupBy === 'scope') {
        return { key: problem.scope || '—', label: this.scopeLabel(problem.scope) };
      }
      if (this.groupBy === 'status') {
        return { key: problem.status, label: this.statusLabel(problem.status) };
      }
      return { key: problem.disposition || '—', label: this.dispositionLabel(problem.disposition) };
    },
    clearFilters() {
      for (const d of DIMENSIONS) this.selected[d.key] = [];
    },
    toggleInitiative(value) {
      const i = this.selected.initiative.indexOf(value);
      if (i === -1) this.selected.initiative.push(value);
      else this.selected.initiative.splice(i, 1);
    },
    labelize(value) {
      if (!value) return '—';
      return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    categoryLabel(value) {
      if (!value) return '—';
      return this.categoryLabelMap[value] || this.labelize(value);
    },
    subcategoryLabel(value) {
      if (!value) return '—';
      return this.subcategoryLabelMap[value] || this.labelize(value);
    },
    gapTypeLabel(value) {
      return this.labelize(value);
    },
    dispositionLabel(value) {
      return this.labelize(value);
    },
    scopeLabel(value) {
      return this.labelize(value);
    },
    initiativeLabel(value) {
      if (!value) return '—';
      return INITIATIVE_LABELS[value] || this.labelize(value);
    },
    categoryStyle(category) {
      return CATEGORY_STYLE[category] || NEUTRAL_STYLE;
    },
    gapTypeStyle(gapType) {
      return GAP_TYPE_STYLE[gapType] || NEUTRAL_STYLE;
    },
    dispositionStyle(disposition) {
      return DISPOSITION_STYLE[disposition] || NEUTRAL_STYLE;
    },
    statusStyleFor(status) {
      return STATUS_STYLE[status] || NEUTRAL_STYLE;
    },
    isDimmed(problem) {
      return problem.status === 'resolved' || problem.status === 'dropped';
    },
  },
};
</script>
